import { Vector2 } from './vector';
import { Body } from './body';

export interface WorldConfig {
  G: number;
  softening: number;
  dt: number;
  trailLength: number;
  collisionElasticity: number;
  enableCollisions: boolean;
}

export const DEFAULT_CONFIG: WorldConfig = {
  G: 0.5,
  softening: 4,
  dt: 0.5,
  trailLength: 120,
  collisionElasticity: 0.9,
  enableCollisions: true,
};

export class PhysicsWorld {
  bodies: Body[] = [];
  config: WorldConfig;
  time = 0;

  constructor(config: Partial<WorldConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  addBody(body: Body): void {
    this.bodies.push(body);
  }

  removeBody(id: number): void {
    this.bodies = this.bodies.filter((b) => b.id !== id);
  }

  clear(): void {
    this.bodies = [];
    this.time = 0;
  }

  step(): void {
    const { G, softening, dt, trailLength, enableCollisions, collisionElasticity } = this.config;
    const n = this.bodies.length;

    // Compute accelerations
    const accs: Vector2[] = new Array(n);
    for (let i = 0; i < n; i++) {
      accs[i] = new Vector2(0, 0);
    }

    for (let i = 0; i < n; i++) {
      const bi = this.bodies[i];
      if (bi.fixed) continue;
      for (let j = i + 1; j < n; j++) {
        const bj = this.bodies[j];
        const dx = bj.pos.x - bi.pos.x;
        const dy = bj.pos.y - bi.pos.y;
        const distSq = dx * dx + dy * dy + softening * softening;
        const dist = Math.sqrt(distSq);
        const force = G / distSq;

        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;

        if (!bi.fixed) {
          accs[i].x += fx * bj.mass;
          accs[i].y += fy * bj.mass;
        }
        if (!bj.fixed) {
          accs[j].x -= fx * bi.mass;
          accs[j].y -= fy * bi.mass;
        }
      }
    }

    // Symplectic Euler integration
    for (let i = 0; i < n; i++) {
      const b = this.bodies[i];
      if (b.fixed) continue;
      b.vel.x += accs[i].x * dt;
      b.vel.y += accs[i].y * dt;
      b.pos.x += b.vel.x * dt;
      b.pos.y += b.vel.y * dt;

      // Update trail
      if (this.time % 3 === 0) {
        b.trail.push(b.pos.clone());
        if (b.trail.length > trailLength) {
          b.trail.shift();
        }
      }
    }

    // Collisions
    if (enableCollisions) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const a = this.bodies[i];
          const b = this.bodies[j];
          const dx = b.pos.x - a.pos.x;
          const dy = b.pos.y - a.pos.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const minDist = a.radius + b.radius;

          if (dist < minDist && dist > 0) {
            // Separate
            const overlap = minDist - dist;
            const nx = dx / dist;
            const ny = dy / dist;
            if (!a.fixed) {
              a.pos.x -= nx * overlap * 0.5;
              a.pos.y -= ny * overlap * 0.5;
            }
            if (!b.fixed) {
              b.pos.x += nx * overlap * 0.5;
              b.pos.y += ny * overlap * 0.5;
            }

            // Elastic collision response
            if (!a.fixed || !b.fixed) {
              const dvx = b.vel.x - a.vel.x;
              const dvy = b.vel.y - a.vel.y;
              const velAlongNormal = dvx * nx + dvy * ny;

              if (velAlongNormal < 0) {
                const e = collisionElasticity;
                const invMassA = a.fixed ? 0 : 1 / a.mass;
                const invMassB = b.fixed ? 0 : 1 / b.mass;
                const impulse = -(1 + e) * velAlongNormal / (invMassA + invMassB);

                if (!a.fixed) {
                  a.vel.x -= impulse * invMassA * nx;
                  a.vel.y -= impulse * invMassA * ny;
                }
                if (!b.fixed) {
                  b.vel.x += impulse * invMassB * nx;
                  b.vel.y += impulse * invMassB * ny;
                }
              }
            }
          }
        }
      }
    }

    this.time++;
  }

  computeEnergy(): number {
    let kinetic = 0;
    let potential = 0;
    const { G, softening } = this.config;

    for (const b of this.bodies) {
      kinetic += 0.5 * b.mass * (b.vel.x * b.vel.x + b.vel.y * b.vel.y);
    }

    for (let i = 0; i < this.bodies.length; i++) {
      for (let j = i + 1; j < this.bodies.length; j++) {
        const a = this.bodies[i];
        const b = this.bodies[j];
        const dist = a.pos.dist(b.pos) + softening;
        potential -= (G * a.mass * b.mass) / dist;
      }
    }

    return kinetic + potential;
  }

  // Predict position after steps without mutating
  predict(body: Body, steps: number): Vector2[] {
    const points: Vector2[] = [];
    let pos = body.pos.clone();
    let vel = body.vel.clone();
    const { G, softening, dt } = this.config;

    for (let s = 0; s < steps; s++) {
      let ax = 0;
      let ay = 0;
      for (const other of this.bodies) {
        if (other.id === body.id) continue;
        const dx = other.pos.x - pos.x;
        const dy = other.pos.y - pos.y;
        const distSq = dx * dx + dy * dy + softening * softening;
        const dist = Math.sqrt(distSq);
        const f = (G * other.mass) / distSq;
        ax += (dx / dist) * f;
        ay += (dy / dist) * f;
      }
      vel.x += ax * dt;
      vel.y += ay * dt;
      pos.x += vel.x * dt;
      pos.y += vel.y * dt;
      if (s % 3 === 0) points.push(pos.clone());
    }
    return points;
  }
}
